/**
 * AuditHub - Main container for the professional audit management system
 * 
 * Sub-modules:
 *   Dashboard  — Real-time KPIs, compliance trends, overdue actions
 *   Templates  — Build, version, and manage audit templates
 *   Audits     — List, execute, and review audits
 *   Findings   — Non-conformance tracker, CAPA workflow, user assignments
 *   Calendar   — Scheduled audits, recurring programmes, calendar view
 *   Risk       — Policy-led risk register, likelihood × consequence matrix
 *   Compliance — ISO 45001/9001 clause mapping, regulation tracking
 */

import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  FileText,
  ClipboardCheck,
  AlertTriangle,
  CalendarDays,
  ShieldAlert,
  Scale,
  Upload,
} from 'lucide-react';
import { useAuth } from '@shared/contexts/AuthContext';
import { supabase } from '@shared/supabaseClient';

// Sub-module lazy imports
import AuditDashboard from './AuditDashboard';
import AuditTemplateManager from './AuditTemplateManager';
import AuditList from './AuditList';
import AuditFindings from './AuditFindings';
import AuditCalendar from './AuditCalendar';
import AuditRiskEngine from './AuditRiskEngine';
import AuditCompliance from './AuditCompliance';
import AuditDocUpload from './AuditDocUpload';

const TABS = [
  { id: 'dashboard',  label: 'Dashboard',   icon: LayoutDashboard,  description: 'KPIs & performance' },
  { id: 'templates',  label: 'Templates',   icon: FileText,         description: 'Build & manage' },
  { id: 'audits',     label: 'Audits',      icon: ClipboardCheck,   description: 'Execute & review' },
  { id: 'upload',     label: 'Upload',      icon: Upload,           description: 'PDF/Word audits' },
  { id: 'findings',   label: 'Findings',    icon: AlertTriangle,    description: 'Audit actions & findings' },
  { id: 'calendar',   label: 'Calendar',    icon: CalendarDays,     description: 'Schedule & plan' },
  { id: 'risk',       label: 'Risk Engine', icon: ShieldAlert,      description: 'Risk register' },
  { id: 'compliance', label: 'Compliance',  icon: Scale,            description: 'ISO & regulatory' },
];

export default function AuditHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [pendingExtensionCount, setPendingExtensionCount] = useState(0);

  // Fetch pending extension request count for tab badge
  useEffect(() => {
    if (!user?.id) return;
    const fetchCount = async () => {
      try {
        const { count, error } = await supabase
          .from('action_extension_requests')
          .select('id, audit_actions!inner(assigned_by)', { count: 'exact', head: true })
          .eq('status', 'pending')
          .eq('audit_actions.assigned_by', user.id);
        if (!error) setPendingExtensionCount(count || 0);
      } catch { /* silent */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [user?.id]);

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':  return <AuditDashboard user={user} />;
      case 'templates':  return <AuditTemplateManager user={user} onNavigate={setActiveTab} />;
      case 'audits':     return <AuditList user={user} onNavigate={setActiveTab} />;
      case 'upload':     return <AuditDocUpload user={user} />;
      case 'findings':   return <AuditFindings user={user} />;
      case 'calendar':   return <AuditCalendar user={user} />;
      case 'risk':       return <AuditRiskEngine user={user} />;
      case 'compliance': return <AuditCompliance user={user} />;
      default:           return <AuditDashboard user={user} />;
    }
  };

  const currentTab = TABS.find(t => t.id === activeTab);

  return (
    <div className="h-full flex flex-col">
      {/* Top navigation bar */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="px-6 py-3">
          {/* Title row */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <ShieldAlert className="h-6 w-6 text-amber-500" />
                Audit Hub
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {currentTab?.description || 'Safety, quality & compliance management'}
              </p>
            </div>
          </div>

          {/* Tab bar */}
          <nav className="flex gap-2 overflow-x-auto pb-px -mb-px" role="tablist">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-lg
                    whitespace-nowrap transition-all duration-200 border
                    ${isActive
                      ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-foreground/80 shadow-md shadow-amber-500/10 scale-[1.02]'
                      : 'bg-muted/30 text-muted-foreground border-foreground/20 hover:bg-muted/60 hover:text-foreground hover:border-foreground/50 hover:shadow-sm hover:scale-[1.03] hover:-translate-y-0.5'}
                  `}
                >
                  <Icon className={`h-4 w-4 ${isActive ? 'text-amber-500' : ''}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {tab.id === 'findings' && pendingExtensionCount > 0 && (
                    <span className="ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white animate-pulse">
                      {pendingExtensionCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-y-auto">
        {renderContent()}
      </div>
    </div>
  );
}
